import BundlePriceBreakdown from '@/components/catalogue/bundle/BundlePriceBreakdown';
import { Counter } from '@/components/form/fields/number/Counter';
import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { Picture } from '@/components/image/Image';
import { Card } from '@/components/sections/Card';
import { Product } from '@/services/api/contracts/catalog/types';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { useBundleProducts } from '@/services/bundles/useBundleProducts';
import { usePrintCurrencyStringForCurrentCustomer } from '@/utils/price';
import { FC, FunctionComponent, useMemo } from 'react';
import { BundleWithQuantity } from './CreateFromBundlesModal';

interface BundleProps {
  bundleWithQuantity: BundleWithQuantity;
  onSelectCallback: (bundleId: string, quantity: number) => void;
}

interface ProductImageProps {
  product: Product;
}

export const ProductImage: FC<ProductImageProps> = ({ product }) => {
  return product.imageUri?.id ? (
    <div className="w-full h-[150px] flex items-center justify-center">
      <Picture image={product.imageUri} containerHeight={150} containerWidth={175}></Picture>
    </div>
  ) : (
    <div className="w-full h-[150px] [&_svg]:w-[100%] [&_svg]:h-[100%]">
      {CategoryIcons[product.category.description]}
    </div>
  );
};

const displayPeripherals = (peripherals: string[]): string => {
  if (peripherals.length === 0) return '';
  if (peripherals.length === 1) return peripherals[0];

  const totalLength = peripherals.reduce((acc, name) => acc + name.length, 0);
  if (totalLength <= 30) {
    return peripherals.join(', ');
  }

  let currentLength = 0;
  const displayNames: string[] = [];

  for (const peripheral of peripherals) {
    if (currentLength + peripheral.length <= 30) {
      displayNames.push(peripheral);
      currentLength += peripheral.length + 2; // Add 2 for the comma and space
    }
  }

  if (displayNames.length === 1) {
    const remainingPeripherals = peripherals.length - 1;

    return `${peripherals[0]} and ${remainingPeripherals} ${remainingPeripherals === 1 ? 'other' : 'others'}`;
  } else {
    return `${displayNames.join(', ')} and ${peripherals.length - displayNames.length} others`;
  }
};

const BundleSelectionCard: FunctionComponent<BundleProps> = ({ bundleWithQuantity, onSelectCallback }) => {
  const bundle = bundleWithQuantity.bundle;

  const { mainDevice, peripherals } = useBundleProducts(bundle.mainDevice, bundle.bundledItems);

  const peripheralNames = useMemo(() => displayPeripherals(peripherals.map(item => item.name)), [peripherals]);

  const totalPrice = useMemo(() => {
    const mainDevicePrice = mainDevice?.price?.price ?? 0;
    const peripheralPrices = peripherals.reduce((total, currentItem) => {
      const count = bundle.bundledItems.find(item => item.catalogItem === currentItem.id)?.count;

      return total + (count ?? 1) * (currentItem.price?.price ?? 0);
    }, 0);

    return mainDevicePrice + peripheralPrices;
  }, [mainDevice, peripherals, bundle]);

  const printCurrencyString = usePrintCurrencyStringForCurrentCustomer();
  const cost = printCurrencyString(totalPrice);

  const role = useRole();

  return (
    <>
      <Card>
        <div className="flex flex-row space-x-5 w-full h-[150px] items-center">
          <div className="border-r border-secondary-std w-[200px] h-full flex items-center">
            {mainDevice && <ProductImage product={mainDevice} />}
          </div>
          <div className="flex-1 py-2">
            <p className="text-2xl font-semibold">{bundle.name}</p>
            <div className="flex flex-row space-x-10">
              <span className="w-24">Main Device</span>
              <span className="text-primary-500">{mainDevice?.name || 'Unknown'}</span>
            </div>
            <div className="flex flex-row space-x-10">
              <span className="w-24">Peripherals</span>
              <span className="text-primary-500">{peripherals.length > 0 ? peripheralNames : 'N/A'}</span>
            </div>
            <div className="text-2xl mt-3 text-primary-800 flex space-x-1 items-center">
              <span>{cost}</span>
            </div>
            {role.type !== RoleType.Company && (
              <BundlePriceBreakdown
                bundle={bundle}
                price={totalPrice}
                products={mainDevice ? [mainDevice, ...peripherals] : peripherals}
              />
            )}
          </div>
          <div>
            {' '}
            <Counter
              className="flex justify-center py-2"
              id={bundle.id}
              value={bundleWithQuantity.quantity}
              allowManualChange={true}
              min={0}
              onChange={value => {
                onSelectCallback(bundle.id, value);
              }}
            />
          </div>
        </div>
      </Card>
    </>
  );
};

export default BundleSelectionCard;
